/**
 * Enfan de Palestine — comportements du thème.
 * Chaque fonctionnalité est un élément personnalisé (custom element) autonome :
 * sans JavaScript, la page reste utilisable (liens et formulaires classiques).
 */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Compteur du panier (en-tête) ---------- */
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    const previous = Number(element.dataset.cartCount);
    element.dataset.cartCount = String(count);
    element.textContent = element.dataset.cartLabel.replace('__count__', String(count));

    if (count > previous) {
      element.classList.add('is-pulsing');
      setTimeout(() => element.classList.remove('is-pulsing'), 500);
    }
  });
}

/* ---------- Menu mobile plein écran ---------- */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', () => this.setOpen(!this.hasAttribute('data-menu-open')));
    this.panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => this.setOpen(false)));
    this.onKeyDown = (event) => {
      if (event.key === 'Escape') {
        this.setOpen(false);
        this.toggle.focus();
      }
    };

    // Passage en affichage ordinateur (tablette tournée) : on referme le menu,
    // sinon la page resterait inactive derrière un panneau masqué.
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.desktopQuery.addEventListener('change', (event) => {
      if (event.matches && this.hasAttribute('data-menu-open')) this.setOpen(false);
    });

    // Accueil : barre transparente au-dessus de la grande image tant qu'on n'a pas défilé.
    if (this.hasAttribute('data-transparent-home')) {
      this.updateTransparency = () => {
        const transparent = window.scrollY <= 40 && !this.hasAttribute('data-menu-open');
        this.toggleAttribute('data-transparent', transparent);
      };
      window.addEventListener('scroll', this.updateTransparency, { passive: true });
      this.updateTransparency();
    }
  }

  setOpen(open) {
    this.toggleAttribute('data-menu-open', open);
    this.panel.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen;
    this.updateTransparency?.();
    document.documentElement.style.overflow = open ? 'hidden' : '';
    this.setPageInert(open);

    if (open) {
      window.addEventListener('keydown', this.onKeyDown);
    } else {
      window.removeEventListener('keydown', this.onKeyDown);
    }
  }

  // Menu ouvert : la page derrière le panneau devient inaccessible au clavier
  // et aux lecteurs d'écran (comme une fenêtre modale). Seuls la barre du menu
  // et le panneau restent utilisables.
  setPageInert(inert) {
    if (!inert) {
      this.inertElements?.forEach((element) => (element.inert = false));
      this.inertElements = [];
      return;
    }
    this.inertElements = [];
    for (let node = this; node.parentElement && node !== document.body; node = node.parentElement) {
      [...node.parentElement.children].forEach((sibling) => {
        if (sibling === node || sibling.inert || sibling.matches('script, style, link, template')) return;
        sibling.inert = true;
        this.inertElements.push(sibling);
      });
    }
  }
}
customElements.define('header-menu', HeaderMenu);

/* ---------- Sélecteur de pays / devise : envoi automatique ---------- */
class LocalizationSelect extends HTMLElement {
  connectedCallback() {
    const select = this.querySelector('select');
    const form = this.querySelector('form');
    const button = this.querySelector('button');
    if (button) button.hidden = true;
    select?.addEventListener('change', () => form?.submit());
  }
}
customElements.define('localization-select', LocalizationSelect);

/* ---------- Lien « Retour » ---------- */
class HistoryBack extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('a');
    link?.addEventListener('click', (event) => {
      if (window.history.length > 1 && document.referrer.startsWith(window.location.origin)) {
        event.preventDefault();
        window.history.back();
      }
    });
  }
}
customElements.define('history-back', HistoryBack);

/* ---------- Message masquable (barre de promotion) ---------- */
class DismissibleMessage extends HTMLElement {
  connectedCallback() {
    const key = `edp-dismissed-${this.dataset.key}`;
    try {
      if (window.sessionStorage.getItem(key)) this.hidden = true;
    } catch (error) {
      // Stockage indisponible (navigation privée) : on affiche simplement le message.
    }

    this.querySelector('button')?.addEventListener('click', () => {
      this.hidden = true;
      try {
        window.sessionStorage.setItem(key, '1');
      } catch (error) {
        // Rien à faire.
      }
    });
  }
}
customElements.define('dismissible-message', DismissibleMessage);

/* ---------- Carrousel plein écran de l'accueil ----------
 * Fondu entre les images, barres de progression (une par image), pause au
 * survol / au focus / onglet caché / bouton pause, glisser au doigt et flèches.
 */
class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.steps = [...this.querySelectorAll('[data-step]')];
    if (this.slides.length < 2) return;

    this.index = 0;
    this.autoplay = this.hasAttribute('data-autoplay') && !prefersReducedMotion.matches;
    this.userPaused = false;
    this.hovered = false;
    this.focused = false;
    this.pauseButton = this.querySelector('[data-pause]');
    this.style.setProperty('--hero-duration', `${Number(this.dataset.duration) || 6000}ms`);
    this.toggleAttribute('data-autoplay-active', this.autoplay);
    if (!this.autoplay && this.pauseButton) this.pauseButton.hidden = true;

    this.steps.forEach((step) => step.addEventListener('click', () => this.goTo(Number(step.dataset.step))));
    this.addEventListener('animationend', (event) => {
      if (event.animationName === 'hero-progress' && event.target.closest('[data-step]') === this.steps[this.index]) {
        this.goTo(this.index + 1);
      }
    });

    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
      );
      this.toggleAttribute('data-user-paused', this.userPaused);
      this.updatePlayState();
    });

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') {
        this.hovered = true;
        this.updatePlayState();
      }
    });
    this.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.updatePlayState();
    });
    this.addEventListener('focusin', () => {
      this.focused = true;
      this.updatePlayState();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focused = false;
        this.updatePlayState();
      }
    });
    document.addEventListener('visibilitychange', () => this.updatePlayState());

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    });

    let start = null;
    this.addEventListener(
      'touchstart',
      (event) => {
        start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      },
      { passive: true }
    );
    this.addEventListener('touchend', (event) => {
      if (!start) return;
      const deltaX = event.changedTouches[0].clientX - start.x;
      const deltaY = event.changedTouches[0].clientY - start.y;
      start = null;
      if (Math.abs(deltaX) >= 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.goTo(this.index + (deltaX < 0 ? 1 : -1));
      }
    });

    this.render();
    this.updatePlayState();
  }

  goTo(target) {
    const total = this.slides.length;
    const next = ((target % total) + total) % total;
    if (next === this.index) return;
    this.index = next;
    this.render();
  }

  render() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    this.steps.forEach((step, i) => {
      step.dataset.state = i < this.index ? 'done' : i === this.index ? 'current' : 'todo';
      if (i === this.index) {
        step.setAttribute('aria-current', 'true');
        // On remplace la barre pour relancer son animation depuis le début.
        const fill = step.querySelector('.hero-carousel__fill');
        fill.replaceWith(fill.cloneNode());
      } else {
        step.removeAttribute('aria-current');
      }
    });
  }

  updatePlayState() {
    const playing = this.autoplay && !this.userPaused && !this.hovered && !this.focused && !document.hidden;
    this.toggleAttribute('data-paused', !playing);
  }
}
customElements.define('hero-carousel', HeroCarousel);

/* ---------- Dates estimées : livraison et précommande ----------
 * Remplace le texte de secours (« livraison en 2 à 5 jours ouvrés ») par de
 * vraies dates calculées à partir d'aujourd'hui : en jours ouvrés (sans
 * samedi ni dimanche) pour la livraison, en semaines pour les précommandes.
 * Modèle de phrase dans data-template, avec [from] et [to].
 */
function addBusinessDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0 && result.getDay() !== 6) added += 1;
  }
  return result;
}

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min);
    const max = Number(this.dataset.max);
    const template = this.dataset.template;
    if (!template || !Number.isFinite(min) || !Number.isFinite(max) || max < min) return;

    const today = new Date();
    const inWeeks = this.dataset.unit === 'weeks';
    let from;
    let to;
    if (inWeeks) {
      from = new Date(today);
      from.setDate(from.getDate() + min * 7);
      to = new Date(today);
      to.setDate(to.getDate() + max * 7);
    } else {
      from = addBusinessDays(today, min);
      to = addBusinessDays(today, max);
    }

    // « entre le vendredi 24 et le vendredi 31 octobre » : le mois n'est
    // répété que si les deux dates tombent dans des mois différents.
    const locale = document.documentElement.lang || 'fr';
    const weekday = inWeeks ? {} : { weekday: 'long' };
    const full = new Intl.DateTimeFormat(locale, { ...weekday, day: 'numeric', month: 'long' });
    const short = new Intl.DateTimeFormat(locale, { ...weekday, day: 'numeric' });
    const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
    this.textContent = template
      .replace('[from]', (sameMonth ? short : full).format(from))
      .replace('[to]', full.format(to));
  }
}
customElements.define('delivery-estimate', DeliveryEstimate);

/* ---------- Visionneuse de photos (photos du projet, fiche produit) ----------
 * Toucher une vignette ouvre la photo en grand. On passe à la suivante en
 * glissant (téléphone), avec les flèches à l'écran ou du clavier ; on ferme
 * avec ×, Échap ou en touchant à côté de la photo.
 */
class PhotoViewer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.track = this.querySelector('[data-viewer-track]');
    if (!this.dialog || !this.track) return;

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-viewer-open]');
      if (opener) {
        this.open(Number(opener.dataset.viewerOpen));
        return;
      }
      if (!this.dialog.open) return;

      const step = event.target.closest('[data-viewer-step]');
      if (step) {
        this.go(Number(step.dataset.viewerStep));
      } else if (event.target.closest('[data-viewer-close]') || !event.target.closest('img, button')) {
        this.dialog.close();
      }
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      this.go(event.key === 'ArrowRight' ? 1 : -1);
    });
  }

  open(index) {
    // Les grandes photos ne sont téléchargées qu'à la première ouverture.
    this.dialog.querySelectorAll('img[data-src]').forEach((img) => {
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    });
    this.dialog.showModal();
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'instant' });
  }

  go(direction) {
    const last = this.track.children.length - 1;
    const current = Math.round(this.track.scrollLeft / this.track.clientWidth);
    const target = Math.min(last, Math.max(0, current + direction));
    this.track.scrollTo({
      left: target * this.track.clientWidth,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
  }
}
customElements.define('photo-viewer', PhotoViewer);

/* ---------- Listes dépliantes à une seule partie ouverte ----------
 * (À propos sur téléphone, FAQ, onglets de la fiche produit.) Ouvrir une
 * partie referme celle du dessus : la page remonte d'autant et le début du
 * texte pouvait passer au-dessus de l'écran (on ne voyait que la photo, il
 * fallait remonter). On ramène alors le titre ouvert juste sous la barre du
 * menu.
 */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.name) return;
    const summary = details.querySelector('summary');
    if (!summary) return;

    const bar = document.querySelector('.header__bar');
    const barBottom = bar && getComputedStyle(bar).display !== 'none' ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
    const top = summary.getBoundingClientRect().top;
    if (top < barBottom) {
      window.scrollBy({ top: top - barBottom - 12, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    }
  },
  true
);

/* ---------- Galerie produit mobile : points de pagination ---------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = this.querySelector('[data-slides]');
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (!this.slides || this.dots.length === 0) return;

    this.slides.addEventListener(
      'scroll',
      () => {
        const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
        this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      },
      { passive: true }
    );
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Ajout au panier (fiche produit et ajout rapide) ----------
 * Ajoute la variante (FormData avec id et quantity), ouvre le panneau de
 * confirmation et met à jour le compteur du panier. On demande en même temps
 * à Shopify le contenu du panneau, calculé avec le panier mis à jour : une
 * seule requête. beforeNotify est appelé juste avant d'ouvrir le panneau.
 */
async function addToCart(body, { fallbackError = '', beforeNotify } = {}) {
  const notification = document.querySelector('cart-notification');
  if (notification) {
    body.append('sections', 'cart-notification');
    body.append('sections_url', window.location.pathname);
  }

  const response = await fetch(`${window.routes.cartAdd}.js`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body,
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.description || data.message || fallbackError);
  }

  beforeNotify?.();

  // À partir d'ici, l'article est dans le panier : si l'affichage échoue
  // (réseau coupé…), on n'annonce surtout pas un échec de l'ajout, sinon le
  // client recommencerait et aurait deux articles.
  try {
    let count = NaN;
    const sectionHtml = data.sections?.['cart-notification'];
    if (notification && sectionHtml) {
      count = notification.open(data.items ?? [data], sectionHtml);
    }
    if (!Number.isFinite(count)) {
      const cart = await fetch(`${window.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );
      count = cart.item_count;
    }
    if (Number.isFinite(count)) updateCartCount(count);
  } catch (error) {
    // Compteur ou panneau non mis à jour : l'ajout, lui, a réussi.
  }
  return data;
}

/* ---------- Ajout rapide depuis les cartes produits (bouton « + ») ----------
 * Produit à taille unique : ajout direct. Sinon, tiroir des tailles ;
 * toucher une taille l'ajoute au panier et ouvre le panneau de confirmation.
 */
class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.titleElement = this.querySelector('[data-quick-add-title]');
    this.priceElement = this.querySelector('[data-quick-add-price]');
    this.preorderElement = this.querySelector('[data-quick-add-preorder]');
    this.sizesElement = this.querySelector('[data-quick-add-sizes]');
    this.errorElement = this.querySelector('[data-quick-add-error]');
    this.linkElement = this.querySelector('[data-quick-add-link]');

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-add]');
      if (trigger) this.onTrigger(trigger);
    });

    this.sizesElement.addEventListener('click', (event) => {
      const button = event.target.closest('[data-variant-id]');
      if (button && !button.disabled) this.add(button.dataset.variantId, button);
    });
  }

  onTrigger(trigger) {
    const variants = JSON.parse(trigger.dataset.variants || '[]');

    if (trigger.hasAttribute('data-single')) {
      const variant = variants.find((item) => item.available);
      if (variant) {
        this.add(variant.id, trigger).catch(() => {
          // Ajout impossible (stock épuisé entre-temps…) : la fiche produit explique pourquoi.
          window.location.href = trigger.dataset.url;
        });
      }
      return;
    }

    this.titleElement.textContent = trigger.dataset.title;
    this.priceElement.textContent = trigger.dataset.price;
    if (this.preorderElement) this.preorderElement.hidden = !trigger.hasAttribute('data-preorder');
    this.linkElement.href = trigger.dataset.url;
    this.errorElement.hidden = true;

    this.sizesElement.classList.toggle('size-sheet__sizes--five', variants.length === 5);
    this.sizesElement.replaceChildren(
      ...variants.map((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'size-sheet__size';
        button.dataset.variantId = variant.id;
        button.textContent = variant.title;
        if (!variant.available) {
          button.disabled = true;
          const hint = document.createElement('span');
          hint.className = 'visually-hidden';
          hint.textContent = ` (${this.dataset.labelUnavailable})`;
          button.append(hint);
        }
        return button;
      })
    );

    this.dialog.showModal();
  }

  async add(variantId, button) {
    if (this.busy) return;
    this.busy = true;
    button.setAttribute('aria-busy', 'true');
    this.errorElement.hidden = true;

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    try {
      await addToCart(body, {
        beforeNotify: () => {
          if (this.dialog.open) this.dialog.close();
        },
      });
    } catch (error) {
      if (!this.dialog.open) throw error;
      this.errorElement.textContent = error.message;
      this.errorElement.hidden = false;
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('quick-add', QuickAdd);

/* ---------- Fiche produit (ordinateur) : colonne d'infos qui suit la page ----------
 * Colonne plus courte que l'écran : elle reste en haut pendant qu'on fait
 * défiler les photos. Plus haute : elle défile avec la page jusqu'à ce que son
 * bas soit visible, puis reste en place. Une seule barre de défilement.
 */
document.querySelectorAll('[data-sticky-panel]').forEach((panel) => {
  const gap = 24;
  const update = () => {
    const top = Math.min(gap, window.innerHeight - panel.offsetHeight - gap);
    panel.style.setProperty('--panel-top', `${top}px`);
  };
  new ResizeObserver(update).observe(panel);
  window.addEventListener('resize', update);
  update();
});

/* ---------- Formulaire produit : tailles, ajout au panier, barre fixe, tiroir de tailles ----------
 * Pas de taille présélectionnée (maquette) : « Add to cart » sans taille affiche
 * « Choisis ta taille ». Sur mobile, la barre fixe affiche « Choisir une taille »
 * et ouvre un tiroir ; choisir une taille dans le tiroir l'ajoute au panier.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.idInput = this.querySelector('input[name="id"]');
    this.mainButtons = [...document.querySelectorAll(`[data-product-submit="${this.dataset.section}"]`)];
    this.stickyButton = document.querySelector(`[data-sticky-submit="${this.dataset.section}"]`);
    this.sizeSheet = document.querySelector(`[data-size-sheet="${this.dataset.section}"]`);
    this.priceElements = [...document.querySelectorAll(`[data-product-price="${this.dataset.section}"]`)];
    this.errorElement = this.querySelector('[data-product-error]');
    this.sizeError = this.querySelector('[data-size-error]');
    this.sizeCharts = [...document.querySelectorAll(`[data-size-chart="${this.dataset.section}"]`)];
    this.labels = {
      add: this.dataset.labelAdd,
      added: this.dataset.labelAdded,
      soldOut: this.dataset.labelSoldOut,
      unavailable: this.dataset.labelUnavailable,
      choose: this.dataset.labelChoose,
    };

    if (this.idInput.value) {
      this.currentVariant = this.variants.find((item) => String(item.id) === this.idInput.value);
    }

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input]')) this.onOptionChange();
    });

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.stickyButton?.addEventListener('click', () => this.onStickyClick());
    this.setupSizeSheet();
    this.setupStickyBar();
    this.updateUnavailableValues();
    this.markSizeChart();
  }

  // Guide des tailles : la ligne de la taille choisie est mise en avant.
  markSizeChart() {
    const selected = this.selectedOptions();
    this.sizeCharts.forEach((chart) => {
      chart.querySelectorAll('[data-size-row]').forEach((row) => {
        row.classList.toggle('is-current', selected.includes(row.dataset.sizeRow));
      });
    });
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-option-group]')].map(
      (group) => group.querySelector('input:checked')?.value
    );
  }

  hasChosenVariant() {
    return Boolean(this.idInput.value);
  }

  onOptionChange() {
    if (this.sizeError) this.sizeError.hidden = true;
    this.markSizeChart();
    const options = this.selectedOptions();
    if (options.some((value) => value === undefined)) return;

    // variant est vide si la combinaison n'existe pas (ex. « S / Blanc ») :
    // on n'affiche alors plus rien de la variante choisie avant.
    const variant = this.variants.find((item) => item.options.every((value, index) => value === options[index]));
    this.currentVariant = variant;
    this.isMissingCombination = !variant;
    this.idInput.value = variant ? variant.id : '';
    this.updateUnavailableValues();
    this.updateMainButtons();
    this.updateStickyLabel();
    this.updateStockMessage(variant);

    this.priceElements.forEach((element) => {
      if (variant?.available) element.innerHTML = variant.price_html;
      else element.textContent = variant ? this.labels.soldOut : this.labels.unavailable;
      element.classList.toggle('product__price--sold-out', !variant?.available);
    });

    const express = this.querySelector('[data-express]');
    if (express) express.hidden = !variant?.available;

    const backInStock = this.querySelector('back-in-stock');
    if (backInStock) {
      if (variant) backInStock.setVariant(variant.id, variant.available);
      else backInStock.hidden = true;
    }

    const url = new URL(window.location.href);
    if (variant) url.searchParams.set('variant', variant.id);
    else url.searchParams.delete('variant');
    window.history.replaceState({}, '', url.toString());
  }

  // Bouton principal selon la sélection : « Add to cart », « Épuisé » ou
  // « Indisponible » (combinaison inexistante).
  updateMainButtons() {
    const variant = this.currentVariant;
    if (this.hasChosenVariant() && variant) {
      this.setMainButtons(variant.available ? this.labels.add : this.labels.soldOut, !variant.available);
    } else if (this.isMissingCombination) {
      this.setMainButtons(this.labels.unavailable, true);
    } else {
      const anyAvailable = this.variants.some((item) => item.available);
      this.setMainButtons(anyAvailable ? this.labels.add : this.labels.soldOut, !anyAvailable);
    }
  }

  updateStockMessage(variant) {
    const message = this.querySelector('[data-stock-message]');
    if (!message) return;

    const threshold = Number(this.dataset.lowStockThreshold || 0);
    const stock = variant?.stock;
    const isLow = Boolean(variant?.available) && typeof stock === 'number' && stock > 0 && stock <= threshold;

    message.hidden = !isLow;
    if (isLow) {
      message.textContent =
        stock === 1 ? this.dataset.labelLowOne : this.dataset.labelLowOther.replace('__quantity__', String(stock));
    }
  }

  updateUnavailableValues() {
    const groups = [...this.querySelectorAll('[data-option-group]')];
    const selected = this.selectedOptions();

    groups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-option-input]').forEach((input) => {
        const candidate = [...selected];
        candidate[groupIndex] = input.value;
        const available = this.variants.some(
          (item) =>
            item.available &&
            item.options.every((value, index) => candidate[index] === undefined || value === candidate[index])
        );
        input.closest('.product__value')?.classList.toggle('product__value--unavailable', !available);
      });
    });
  }

  setMainButtons(label, disabled) {
    this.mainButtons.forEach((button) => {
      button.disabled = disabled;
      button.textContent = label;
    });
  }

  updateStickyLabel(justAdded = false) {
    if (!this.stickyButton) return;
    const variant = this.currentVariant;
    let label;
    if (justAdded) label = this.labels.added;
    else if (!this.hasChosenVariant() || !variant) label = this.labels.choose;
    else if (!variant.available) label = this.labels.soldOut;
    else if (this.hasAttribute('data-single-variant')) label = this.labels.add;
    else label = `${this.labels.add} · ${variant.options.join(' / ')}`;

    this.stickyButton.textContent = label;
    this.stickyButton.disabled = Boolean(variant && this.hasChosenVariant() && !variant.available);
  }

  showSizeError() {
    if (!this.sizeError) return;
    this.sizeError.hidden = false;
    const group = this.querySelector('[data-option-group]');
    group?.querySelector('input:not(:disabled)')?.focus();
    if (group && !prefersReducedMotion.matches) {
      group.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-6px)' },
          { transform: 'translateX(6px)' },
          { transform: 'translateX(-4px)' },
          { transform: 'translateX(4px)' },
          { transform: 'translateX(0)' },
        ],
        { duration: 400, easing: 'ease-in-out' }
      );
    }
  }

  onStickyClick() {
    if (!this.hasChosenVariant()) {
      if (this.sizeSheet) {
        this.sizeSheet.showModal();
      } else {
        this.querySelector('[data-option-group]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.showSizeError();
      }
      return;
    }
    this.submitForm();
  }

  // requestSubmit n'existe pas avant iOS 16 : on passe alors par le bouton principal.
  submitForm() {
    if (this.form.requestSubmit) this.form.requestSubmit();
    else this.querySelector('[data-main-submit]')?.click();
  }

  setupSizeSheet() {
    if (!this.sizeSheet) return;

    this.sizeSheet.addEventListener('click', (event) => {
      const sizeButton = event.target.closest('[data-sheet-size]');
      if (sizeButton) {
        const input = [...this.querySelectorAll('[data-option-input]')].find(
          (element) => element.value === sizeButton.dataset.sheetSize
        );
        if (!input) return;
        input.checked = true;
        this.onOptionChange();
        this.sizeSheet.close();
        if (this.currentVariant?.available) this.submitForm();
        return;
      }

      const guideButton = event.target.closest('[data-sheet-guide]');
      if (guideButton) {
        this.sizeSheet.close();
        document.getElementById(guideButton.dataset.sheetGuide)?.showModal();
      }
    });
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    if (this.errorElement) this.errorElement.hidden = true;

    if (!this.hasChosenVariant()) {
      this.showSizeError();
      return;
    }

    // Verrou pendant l'ajout : un double appui (connexion lente) n'ajoute
    // pas deux articles. Le bouton principal et la barre fixe sont bloqués.
    this.submitting = true;
    const buttons = [...this.mainButtons, this.stickyButton].filter(Boolean);
    buttons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });

    let added = false;
    try {
      await addToCart(new FormData(this.form), { fallbackError: this.labels.unavailable });
      added = true;
    } catch (error) {
      if (this.errorElement) {
        this.errorElement.textContent = error.message;
        this.errorElement.hidden = false;
      }
    } finally {
      this.submitting = false;
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }

    if (added) {
      this.setMainButtons(this.labels.added, false);
      this.updateStickyLabel(true);
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        this.updateMainButtons();
        this.updateStickyLabel();
      }, 1500);
    } else {
      this.updateMainButtons();
      this.updateStickyLabel();
    }
  }

  setupStickyBar() {
    const mainButton = this.querySelector('[data-main-submit]');
    const bar = document.querySelector(`[data-sticky-buy="${this.dataset.section}"]`);
    if (!mainButton || !bar || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(([entry]) => {
      bar.hidden = entry.isIntersecting;
    });
    observer.observe(mainButton);
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Suggestions du panier ----------
 * Demande à Shopify des produits complémentaires (si l'app Search & Discovery
 * est configurée), sinon des produits similaires, à partir du premier article
 * du panier. Si Shopify ne renvoie rien, la collection de secours, déjà
 * affichée par le serveur, reste en place.
 */
async function fetchRecommendationItems(productId, sectionId = 'cart-recommendations') {
  for (const intent of ['complementary', 'related']) {
    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: productId,
      limit: '10',
      intent,
    });

    try {
      const response = await fetch(`${window.routes.recommendations}?${params}`);
      if (!response.ok) continue;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelector('[data-recommendations-list]')?.children;
      if (items && items.length > 0) return [...items];
    } catch (error) {
      return [];
    }
  }
  return [];
}

class CartRecommendations extends HTMLElement {
  async connectedCallback() {
    const list = this.querySelector('[data-recommendations-list]');
    if (!list) return;

    if (this.dataset.source === 'auto' && this.dataset.productId) {
      const items = await fetchRecommendationItems(this.dataset.productId, this.dataset.section);
      if (items.length > 0) {
        list.replaceChildren(...items);
        this.hidden = false;
      }
    }
    this.sortByFreeShippingGap(list);
  }

  // Sous le seuil de livraison offerte, les suggestions dont le prix est le plus
  // proche de la somme manquante passent en premier (maquette).
  sortByFreeShippingGap(list) {
    const remaining = Number(this.dataset.remaining || 0);
    if (remaining <= 0) return;
    const gap = (item) => Math.abs(Number(item.dataset.price) - remaining);
    list.replaceChildren(...[...list.children].sort((a, b) => gap(a) - gap(b)));
  }
}
customElements.define('cart-recommendations', CartRecommendations);

/* ---------- Panier : « − », « + » et suppression ----------
 * Sans JavaScript, ce sont de simples liens. Avec, un seul changement part à
 * la fois (un double appui ne retire plus deux articles, ni ne mène à la page
 * d'erreur de Shopify), par numéro de ligne comme le thème Dawn : les
 * identifiants d'article de Shopify changent d'un affichage à l'autre. On
 * vérifie d'abord que la ligne porte toujours le même article (panier modifié
 * dans un autre onglet), puis la page se recharge dans tous les cas.
 */
class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.onClick(event));
  }

  async onClick(event) {
    const link = event.target.closest('a[href*="/cart/change"]');
    const item = link?.closest('[data-line]');
    if (!item) return;
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;
    this.setAttribute('aria-busy', 'true');

    const line = Number(item.dataset.line);
    const quantity = Number(new URL(link.href).searchParams.get('quantity'));
    try {
      const cart = await fetch(`${window.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );
      if (String(cart.items[line - 1]?.variant_id) === item.dataset.variantId) {
        await fetch(`${window.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        });
      }
    } catch (error) {
      // Réseau indisponible : le lien prend le relais.
      window.location.href = link.href;
      return;
    }
    window.location.replace(window.routes.cart);
  }
}
customElements.define('cart-items', CartItems);

// Retour arrière vers un panier gardé en mémoire par le navigateur : on le
// recharge, sinon il peut montrer des articles déjà retirés.
window.addEventListener('pageshow', (event) => {
  if (event.persisted && document.querySelector('cart-items')) window.location.reload();
});

/* ---------- Panneau de confirmation d'ajout au panier ---------- */
class CartNotification extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-notif-body]');
    this.cartLink = this.querySelector('[data-notif-cart-link]');
    this.recos = this.querySelector('[data-notif-recos]');
    this.recosList = this.recos?.querySelector('[data-recommendations-list]');

    this.dialog?.addEventListener('click', (event) => {
      // Un clic sur le fond (hors du contenu) ou sur un bouton de fermeture ferme le panneau.
      if (event.target === this.dialog || event.target.closest('[data-notif-close]')) this.dialog.close();
    });
  }

  /**
   * Affiche les articles ajoutés (repérés par leur clé, ou à défaut par leur
   * variante) à partir de la section « cart-notification » renvoyée par
   * Shopify, et renvoie le nombre d'articles du panier.
   */
  open(lineItems, sectionHtml) {
    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const keys = new Set(lineItems.map((lineItem) => lineItem.key));
    const variants = new Set(lineItems.map((lineItem) => String(lineItem.variant_id)));
    const items = [...html.querySelectorAll('[data-notif-item]')].filter(
      (element) => keys.has(element.dataset.notifItem) || variants.has(element.dataset.notifVariant)
    );
    const freeShipping = html.querySelector('[data-free-shipping]');
    const bundleNudge = html.querySelector('[data-bundle-nudge]');
    const count = Number(html.querySelector('[data-notif-count]')?.dataset.notifCount);

    this.body.replaceChildren(...[...items, freeShipping, bundleNudge].filter(Boolean));
    if (Number.isFinite(count)) {
      this.cartLink.textContent = this.cartLink.dataset.label.replace('__count__', String(count));
    }

    this.recos.hidden = true;
    this.recosList.replaceChildren();
    const productId = items[0]?.dataset.notifProduct;
    if (productId) {
      fetchRecommendationItems(productId).then((recommendations) => {
        if (recommendations.length === 0) return;
        this.recosList.replaceChildren(...recommendations);
        this.recos.hidden = false;
      });
    }

    if (!this.dialog.open) this.dialog.showModal();
    return count;
  }
}
customElements.define('cart-notification', CartNotification);

/* ---------- Packs de l'app Bundler : même panneau qu'« Ajouter au panier » ----------
 * Avec le réglage Bundler « Ne pas rediriger », « Ajouter le pack au panier »
 * ajoute les articles sans quitter la page, puis le signale : « product:added »
 * pour chaque article (avec sa variante), et « bndlr:bundle_added_to_cart » à
 * la fin. On ouvre alors le panneau de confirmation avec les articles du pack.
 */
const bundleVariants = new Set();
document.addEventListener('product:added', (event) => {
  if (event.detail?.variant) bundleVariants.add(String(event.detail.variant));
});
document.addEventListener('bndlr:bundle_added_to_cart', () => {
  // Les « product:added » du même ajout peuvent arriver juste après : on attend la fin.
  setTimeout(async () => {
    const lineItems = [...bundleVariants].map((variant) => ({ variant_id: variant }));
    bundleVariants.clear();
    const notification = document.querySelector('cart-notification');
    if (!notification) return;
    try {
      const params = new URLSearchParams({ sections: 'cart-notification' });
      const data = await fetch(`${window.location.pathname}?${params}`).then((res) => res.json());
      const count = notification.open(lineItems, data['cart-notification']);
      if (Number.isFinite(count)) updateCartCount(count);
    } catch (error) {
      // Panneau non affiché : le pack, lui, est bien dans le panier.
    }
  });
});

/* ---------- Packs de l'app Bundler : une carte par offre ----------
 * Sous le bouton d'achat, chaque pack devient une carte fermée dont le titre
 * annonce l'offre (titre et texte réglés dans Bundler, ex. « 2 tees = 70 € ·
 * 3 tees = 100 € »). Un toucher l'ouvre pour composer le pack, une seule
 * ouverte à la fois. Si une taille est choisie, l'article de la page est déjà
 * placé dans le pack : il ne reste qu'à ajouter l'autre. Bundler dessine ses
 * packs après coup (au premier geste du visiteur), parfois plusieurs fois :
 * on prépare chaque pack dès qu'il apparaît.
 */
function setupBundleCards() {
  const target = document.getElementById('bundler-target-element');
  if (!target) return;
  let openBundle = null;

  // Place l'article de la page (taille choisie) dans le pack, par les boutons de Bundler.
  const addCurrentItem = (container) => {
    const variantId = document.querySelector('product-form input[name="id"]')?.value;
    if (!variantId || container.dataset.packCurrentItem === variantId) return;
    const select = [...container.querySelectorAll('.bndlr-select-variant')].find((element) =>
      [...element.options].some((option) => option.value === variantId)
    );
    const product = select?.closest('.bndlr-product');
    const button = product?.querySelector('.bndlr-add-to-bundle, .bndlr-add-to-sectioned-bundle');
    if (!button || button.classList.contains('bndlr-no-click')) return;
    select.value = variantId;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    button.click();
    container.dataset.packCurrentItem = variantId;
  };

  const setOpen = (container, open) => {
    container.classList.toggle('is-open', open);
    container.querySelector('.pack-card__header')?.setAttribute('aria-expanded', String(open));
    if (open) addCurrentItem(container);
  };

  const prepare = (container) => {
    if (container.querySelector('.pack-card__header')) return;
    const title = container.querySelector('.bndlr-bundle-title')?.textContent.trim();
    if (!title) return;
    const text = container.querySelector('.bndlr-bundle-description')?.textContent.trim();
    const bundle = container.closest('[data-bundle]')?.dataset.bundle;

    const header = document.createElement('button');
    header.type = 'button';
    header.className = 'pack-card__header';
    header.setAttribute('aria-expanded', 'false');
    // « 2 tees = 70 € · 3 tees = 100 € » : une offre par ligne.
    const titleElement = document.createElement('span');
    titleElement.className = 'pack-card__title';
    title.split(' · ').forEach((offer) => {
      const line = document.createElement('span');
      line.textContent = offer;
      titleElement.append(line);
    });
    header.append(titleElement);
    if (text) {
      const textElement = document.createElement('span');
      textElement.className = 'pack-card__text';
      textElement.textContent = text;
      header.append(textElement);
    }
    header.addEventListener('click', () => {
      const open = !container.classList.contains('is-open');
      target.querySelectorAll('.pack-card.is-open').forEach((card) => setOpen(card, false));
      setOpen(container, open);
      openBundle = open ? bundle : null;
    });

    container.classList.add('pack-card');
    container.prepend(header);
    // Pack redessiné par Bundler alors qu'il était ouvert : il le reste.
    if (bundle && bundle === openBundle) setOpen(container, true);
  };

  const prepareAll = () => target.querySelectorAll('.bndlr-container').forEach(prepare);
  new MutationObserver(prepareAll).observe(target, { childList: true, subtree: true });
  prepareAll();
}
setupBundleCards();

/* ---------- Alerte de retour en stock (Klaviyo) ----------
 * Envoie l'e-mail du client à Klaviyo (API publique « back in stock »),
 * pour la taille épuisée sélectionnée. Klaviyo envoie l'alerte au retour
 * du stock si « Back in stock » est activé dans le compte Klaviyo.
 */
class BackInStock extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-bis-toggle]');
    this.form = this.querySelector('[data-bis-form]');
    this.message = this.querySelector('[data-bis-message]');

    this.toggle?.addEventListener('click', () => {
      const open = this.form.hidden;
      this.form.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
      if (open) this.form.querySelector('input')?.focus();
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.subscribe();
    });
  }

  setVariant(variantId, available) {
    this.dataset.variantId = String(variantId);
    this.hidden = available;
    this.message.hidden = true;
    this.form.hidden = true;
    this.toggle.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'false');
  }

  async subscribe() {
    const input = this.form.querySelector('input[type="email"]');
    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }

    const submit = this.form.querySelector('button[type="submit"]');
    submit.disabled = true;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(this.dataset.companyId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email: input.value.trim() } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.dataset.variantId}` } },
              },
            },
          }),
        }
      );
      if (!response.ok) throw new Error(String(response.status));
      this.showMessage(this.querySelector('[data-bis-success]').innerHTML);
      this.form.hidden = true;
      this.toggle.hidden = true;
    } catch (error) {
      this.showMessage(this.querySelector('[data-bis-error]').innerHTML);
    } finally {
      submit.disabled = false;
    }
  }

  showMessage(text) {
    this.message.textContent = text.trim();
    this.message.hidden = false;
  }
}
customElements.define('back-in-stock', BackInStock);

/* ---------- Cartes produits : flèches pour parcourir les photos (ordinateur) ----------
 * Les photos suivantes ne sont téléchargées qu'au survol de la carte (souris)
 * ou quand on y arrive au clavier : sur téléphone, où il n'y a pas de
 * flèches, seule la première photo est chargée.
 */
function primeCard(card) {
  card?.classList.add('is-primed');
}
document.addEventListener('pointerover', (event) => {
  if (event.pointerType === 'mouse') primeCard(event.target.closest?.('[data-card]'));
});
document.addEventListener('focusin', (event) => {
  if (event.target.matches?.(':focus-visible')) primeCard(event.target.closest('[data-card]'));
});

document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-card-step]');
  if (!arrow) return;
  event.preventDefault();

  primeCard(arrow.closest('[data-card]'));
  const slides = [...arrow.closest('[data-card]').querySelectorAll('[data-card-slide]')];
  const current = slides.findIndex((slide) => slide.classList.contains('is-active'));
  const next = (current + Number(arrow.dataset.cardStep) + slides.length) % slides.length;
  slides[current]?.classList.remove('is-active');
  slides[next].classList.add('is-active');
});

/* ---------- Boîtes de dialogue simples (guide des tailles…) ---------- */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    document.getElementById(opener.dataset.dialogOpen)?.showModal();
    return;
  }

  const dialog = event.target.closest('dialog.modal');
  if (dialog && (event.target === dialog || event.target.closest('[data-dialog-close]'))) {
    dialog.close();
  }
});

/* ---------- Barre d'annonce ----------
 * Les messages se succèdent (pause au survol ou au clavier, et dans
 * l'éditeur quand un message est sélectionné). Sur téléphone, la barre du
 * menu est fixe : elle se place juste sous la barre d'annonce, puis remonte
 * en haut de l'écran quand on fait défiler (variable --header-offset).
 */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('[data-announcement]')];
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 4000;

    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.updateOffset());
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.updateOffset();

    if (this.messages.length < 2) return;
    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));

    const section = this.closest('.shopify-section');
    section?.addEventListener('shopify:block:select', (event) => {
      const index = this.messages.indexOf(event.target);
      if (index < 0) return;
      this.show(index);
      this.paused = true;
    });
    section?.addEventListener('shopify:block:deselect', () => (this.paused = false));

    // Moins d'animations demandées : pas de changement automatique. Chaque
    // nouvelle page affiche le message suivant, pour que tous restent lus.
    if (prefersReducedMotion.matches) {
      let index = 0;
      try {
        const previous = window.sessionStorage.getItem('edp-announcement');
        index = previous === null ? 0 : (Number(previous) + 1) % this.messages.length || 0;
        window.sessionStorage.setItem('edp-announcement', String(index));
      } catch (error) {
        // Stockage indisponible : premier message.
      }
      this.show(index);
      return;
    }

    this.timer = setInterval(() => {
      if (!this.paused && !document.hidden) this.show(this.index + 1);
    }, this.interval);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.documentElement.style.removeProperty('--header-offset');
  }

  updateOffset() {
    this.frame = 0;
    const offset = Math.max(0, this.offsetHeight - window.scrollY);
    document.documentElement.style.setProperty('--header-offset', `${offset}px`);
  }

  show(index) {
    const next = (index + this.messages.length) % this.messages.length;
    if (next === this.index) return;
    const current = this.messages[this.index];
    const upcoming = this.messages[next];

    current.classList.remove('is-active');
    current.classList.add('is-leaving');
    current.inert = true;
    upcoming.classList.remove('is-leaving');
    upcoming.classList.add('is-active');
    upcoming.inert = false;
    setTimeout(() => current.classList.remove('is-leaving'), 600);
    this.index = next;
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- « Lire la suite » : texte coupé sur téléphone ---------- */
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-read-more]');
    this.button?.addEventListener('click', () => {
      this.classList.add('is-expanded');
      this.button.setAttribute('aria-expanded', 'true');
      this.button.hidden = true;
    });
  }
}
customElements.define('read-more', ReadMore);

/* ---------- Galerie photos : pause de la bande, flèches du carrousel (ordinateur) ---------- */
class PhotoGallery extends HTMLElement {
  connectedCallback() {
    // Bande qui défile toute seule : bouton pause (tout mouvement automatique
    // doit pouvoir être arrêté, y compris au doigt et au clavier).
    this.pauseButton = this.querySelector('[data-gallery-pause]');
    this.pauseButton?.addEventListener('click', () => {
      const paused = !this.hasAttribute('data-user-paused');
      this.toggleAttribute('data-user-paused', paused);
      this.pauseButton.setAttribute('aria-pressed', String(paused));
    });

    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    if (!this.viewport || !this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton.addEventListener('click', () => this.scrollByPage(1));
    this.viewport.addEventListener('scroll', () => this.updateButtons(), { passive: true });
    this.updateButtons();
  }

  scrollByPage(direction) {
    this.viewport.scrollBy({
      left: direction * this.viewport.clientWidth * 0.8,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  updateButtons() {
    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    this.prevButton.disabled = this.viewport.scrollLeft <= 2;
    this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2;
  }
}
customElements.define('photo-gallery', PhotoGallery);

/* ---------- Historique des donations : défilement + garçon qui marche ----------
 * Le garçon au ballon avance sur la frise au rythme du défilement des cartes
 * (début = premier don, fin = dernier), se retourne quand on revient en
 * arrière et ne « marche » que pendant le défilement (jamais si l'utilisateur
 * a demandé moins d'animations).
 */
// Position des pieds dans le dessin, en fraction de sa largeur : c'est ce
// point qui passe sur les carrés de la frise.
const WALKER_ANCHOR_X = 0.7565;
const DONATIONS_STOP_DELAY = 150;
// Défilement minimal (px) avant que le garçon se retourne : en dessous, c'est
// un glissement involontaire (geste vertical qui dévie, carte qui se recale).
const DONATIONS_TURN_DISTANCE = 40;

class DonationsHistory extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-scroller]');
    this.frieze = this.querySelector('[data-frieze]');
    this.walker = this.querySelector('[data-walker]');
    this.facing = this.querySelector('[data-facing]');
    this.prevButton = this.querySelector('[data-donations-prev]');
    this.nextButton = this.querySelector('[data-donations-next]');
    if (!this.scroller) return;

    this.lastScrollLeft = this.scroller.scrollLeft;
    this.frame = 0;
    this.isVisible = false;

    this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.scroller.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollByPage(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
    this.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-donation-dot]');
      if (dot) this.scrollToIndex(Number(dot.dataset.donationDot));
    });

    // Éditeur de thème : sélectionner un don dans la liste l'amène à l'écran.
    this.closest('.shopify-section')?.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest('[data-donation-index]');
      if (card) this.scrollToIndex(Number(card.dataset.donationIndex));
    });

    this.resizeObserver = new ResizeObserver(() => this.schedule());
    this.resizeObserver.observe(this.scroller);
    if (this.frieze) this.resizeObserver.observe(this.frieze);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.isVisible = entry.isIntersecting;
      if (!this.isVisible) this.setWalking(false);
    });
    this.intersectionObserver.observe(this);

    this.schedule();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.stopTimer);
  }

  schedule() {
    if (!this.frame) this.frame = requestAnimationFrame(() => this.render());
  }

  render() {
    this.frame = 0;
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    const scrollLeft = this.scroller.scrollLeft;

    if (this.walker && this.frieze) {
      const progress = maxScroll > 0 ? Math.min(1, Math.max(0, scrollLeft / maxScroll)) : 0;
      // Les pieds vont du premier au dernier carré : le bord gauche du dessin
      // parcourt donc la largeur de la frise moins deux retraits.
      const track = this.frieze.clientWidth - 2 * this.walker.offsetWidth * WALKER_ANCHOR_X;
      this.walker.style.transform = `translate3d(${progress * track}px, 0, 0)`;

      // Sens de la marche : celui du mouvement depuis son début, et seulement
      // au-delà d'une vraie distance. Ainsi, ni le petit recul de l'aimantation
      // des cartes, ni un glissement involontaire ne le font se retourner.
      const distance = scrollLeft - (this.moveStart ?? scrollLeft);
      if (Math.abs(distance) >= DONATIONS_TURN_DISTANCE) {
        this.facing.style.transform = distance < 0 ? 'scaleX(-1)' : '';
      }
    }
    this.lastScrollLeft = scrollLeft;

    if (this.prevButton) this.prevButton.disabled = scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = scrollLeft >= maxScroll - 2;
  }

  onScroll() {
    this.moveStart ??= this.lastScrollLeft;
    this.schedule();
    clearTimeout(this.stopTimer);
    this.stopTimer = setTimeout(() => {
      this.moveStart = null;
      this.setWalking(false);
    }, DONATIONS_STOP_DELAY);
    if (!this.walker || !this.isVisible || prefersReducedMotion.matches) return;
    this.setWalking(true);
  }

  setWalking(walking) {
    this.walker?.classList.toggle('is-walking', walking);
  }

  scrollByPage(direction) {
    this.scroller.scrollBy({
      left: direction * this.scroller.clientWidth * 0.8,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  scrollToIndex(index) {
    const card = this.scroller.querySelector(`[data-donation-index="${index}"]`);
    if (!card) return;
    const padding = parseFloat(getComputedStyle(this.scroller).scrollPaddingLeft) || 0;
    const left =
      card.getBoundingClientRect().left - this.scroller.getBoundingClientRect().left + this.scroller.scrollLeft - padding;
    this.scroller.scrollTo({ left, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
  }
}
customElements.define('donations-history', DonationsHistory);

/* ---------- Page À propos (ordinateur) : le texte change au défilement ----------
 * La page reste fixe ; chaque partie occupe un « palier » de défilement.
 * La partie dont le palier passe au milieu de l'écran devient active.
 * Cliquer sur un titre fait défiler jusqu'à sa partie.
 */
class AboutPinned extends HTMLElement {
  connectedCallback() {
    this.sentinels = [...this.querySelectorAll('[data-about-sentinel]')];
    this.titles = [...this.querySelectorAll('[data-about-title]')];
    this.texts = [...this.querySelectorAll('[data-about-text]')];
    this.photos = [...this.querySelectorAll('[data-about-photo]')];

    this.titles.forEach((title, index) => {
      title.addEventListener('click', () => {
        this.setActive(index);
        this.sentinels[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    this.closest('.shopify-section')?.addEventListener('shopify:block:select', (event) => {
      this.setActive(this.titles.indexOf(event.target));
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(this.sentinels.indexOf(entry.target));
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    this.sentinels.forEach((sentinel) => this.observer.observe(sentinel));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  setActive(index) {
    if (index < 0) return;
    this.titles.forEach((title, i) => title.setAttribute('aria-current', String(i === index)));
    this.texts.forEach((text, i) => {
      text.classList.toggle('is-active', i === index);
      text.inert = i !== index;
    });
    this.photos.forEach((photo, i) => photo.classList.toggle('is-active', i === index));
  }
}
customElements.define('about-pinned', AboutPinned);

/* ---------- Page Archives : carrousel plein écran ----------
 * Une archive à la fois : la photo arrive en fondu par-dessus la précédente,
 * le texte s'efface puis réapparaît. Flèches, points, glissement au doigt et
 * flèches du clavier. L'adresse suit l'archive affichée (?archive=…) pour
 * pouvoir la partager ; un lien partagé ouvre directement la bonne archive.
 * Seules l'archive affichée et ses voisines chargent leur photo.
 */
const ARCHIVES_SWIPE_DISTANCE = 40;

class ArchivesCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-archive-slide]')];
    this.texts = [...this.querySelectorAll('[data-archive-text]')];
    this.dots = [...this.querySelectorAll('[data-archive-dot]')];
    this.total = this.slides.length;
    this.index = 0;
    if (this.total < 2) return;

    this.querySelector('[data-archives-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-archives-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

    this.onKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest?.('input, select, textarea, [contenteditable], dialog[open]')) return;
      event.preventDefault();
      this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    };
    window.addEventListener('keydown', this.onKeyDown);

    this.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      },
      { passive: true }
    );
    this.addEventListener('touchend', (event) => {
      const start = this.touchStart;
      this.touchStart = null;
      if (!start) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) >= ARCHIVES_SWIPE_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.goTo(this.index + (deltaX < 0 ? 1 : -1));
      }
    });

    // Éditeur de thème : sélectionner une archive (dans la liste, sur sa photo
    // ou sur son texte) l'affiche.
    this.closest('.shopify-section')?.addEventListener('shopify:block:select', (event) => {
      const index = [this.slides, this.texts].map((list) => list.indexOf(event.target)).find((i) => i >= 0);
      if (index !== undefined) this.goTo(index, { animate: false, updateUrl: false });
    });

    // Lien partagé (?archive=…) : on ouvre directement sur cette archive.
    const slug = new URL(window.location.href).searchParams.get('archive');
    const start = this.slides.findIndex((slide) => slide.dataset.slug === slug);
    if (start > 0) this.goTo(start, { animate: false, updateUrl: false });
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  wrap(index) {
    return ((index % this.total) + this.total) % this.total;
  }

  goTo(target, { animate = !prefersReducedMotion.matches, updateUrl = true } = {}) {
    const next = this.wrap(target);
    if (next === this.index) return;
    const previous = this.index;
    const near = [this.wrap(next - 1), this.wrap(next + 1)];
    this.index = next;

    // Sans animation (lien partagé, éditeur) : le texte change d'un coup.
    this.classList.toggle('is-instant', !animate);

    this.slides.forEach((slide, index) => {
      slide.classList.toggle('is-current', index === next);
      slide.classList.toggle('is-previous', animate && index === previous);
      slide.classList.toggle('is-near', near.includes(index));
      slide.classList.remove('is-entering');
      slide.setAttribute('aria-hidden', String(index !== next));
    });
    if (animate) {
      const slide = this.slides[next];
      void slide.offsetWidth; // relance l'animation de fondu
      slide.classList.add('is-entering');
    }

    this.texts.forEach((text, index) => text.classList.toggle('is-current', index === next));
    this.dots.forEach((dot, index) => {
      if (index === next) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('archive', this.slides[next].dataset.slug);
      window.history.replaceState({}, '', url.toString());
    }
  }
}
customElements.define('archives-carousel', ArchivesCarousel);

/* ---------- Page Donations : « Voir les autres dons » ---------- */
class DonationsGrid extends HTMLElement {
  connectedCallback() {
    this.more = this.querySelector('[data-more]');
    this.button = this.querySelector('[data-toggle]');
    if (!this.more || !this.button) return;

    // Dons repliés : hors de la navigation au clavier tant qu'ils sont cachés.
    this.more.inert = true;
    this.button.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const expanded = !this.classList.contains('is-expanded');
    this.classList.toggle('is-expanded', expanded);
    this.more.inert = !expanded;
    this.button.setAttribute('aria-expanded', String(expanded));
    this.button.textContent = expanded ? this.button.dataset.labelLess : this.button.dataset.labelMore;
    if (!expanded) {
      this.scrollIntoView({ behavior: prefersReducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }
  }
}
customElements.define('donations-grid', DonationsGrid);

/* ---------- Compteur de dons animé ---------- */
class DonationCounter extends HTMLElement {
  connectedCallback() {
    this.target = Number(this.dataset.amount || 0);
    this.output = this.querySelector('[data-counter-value]');
    if (!this.output) return;

    // Les montants de dons sont toujours en euros : format français (228 500).
    const formatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
    this.format = (value) => formatter.format(value);

    if (this.hasAttribute('data-static') || prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      this.output.textContent = this.format(this.target);
      return;
    }

    this.output.textContent = this.format(0);
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      this.animate();
    });
    observer.observe(this);
  }

  animate() {
    const duration = 1800;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.output.textContent = this.format(Math.round(this.target * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}
customElements.define('donation-counter', DonationCounter);
